import type {
  ToolsTimelineEventActivation,
  ToolsTimelineEventPositions,
  ToolsTimelineExactDates,
  ToolsTimelinePersonAssignments,
  ToolsTimelineYear,
} from '../../../@types/Company';
import { AttributionRole } from '../../../@types/CoproUserAttribution';
import { calendarYearForMonth } from '../../../utils/boitageTimeline';
import { ROUTES } from '../../../utils/routes';

export interface NextAction {
  id: string;
  rowKey: string;
  title: string;
  description: string;
  warning?: string;
  date: Date | null;
  frequencyLabel?: string;
  route: string;
  assignedUserId: number | null;
  urgency: 'overdue' | 'soon' | 'upcoming' | 'recurring';
}

export interface TeamAction {
  id: string;
  rowKey: string;
  /** Planning occurrence shown (e.g. "newsletter-Newsletter 1"), for dated rows only. */
  eventKey?: string;
  title: string;
  description: string;
  date: Date | null;
  frequencyLabel?: string;
  route: string;
  assignedUserId: number;
  status: 'overdue' | 'pending';
}

interface ActionDefinition {
  rowKey: string;
  title: string;
  description: string;
  warning?: string;
  route: string;
  frequency?: 'weekly' | 'quarterly' | 'on-demand';
  frequencyLabel?: string;
  planningEventType?: string;
  planningEventKeys?: string[];
  relevantRoles?: string[];
}

const ROLE_DIRIGEANT = 'dirigeant';

// ToolsTimelineActionCalculator does the same computation on the server for the
// admin views: keep both in step, tests/fixtures/next-actions-cases.json checks them.
// `relevantRoles` must mirror which dashboard sections expose each action's
// module (see ModulesOverview/data.tsx). An action is only surfaced to roles
// whose section actually contains the corresponding module, otherwise a user
// sees actions unrelated to their attribution (e.g. a transactionnaire seeing
// the référent-only DPE action).
const ALL_ACTION_DEFINITIONS: ActionDefinition[] = [
  {
    rowKey: 'newsletter',
    title: 'NEWSLETTER — Envoi trimestriel aux copropriétaires',
    description: 'Envoyez la newsletter trimestrielle aux copropriétaires pour positionner le référent comme expert.',
    route: ROUTES.COMMUNICATION_NEWSLETTER_LIST,
    frequency: 'quarterly',
    planningEventType: 'newsletter',
    planningEventKeys: ['Newsletter 1', 'Newsletter 2', 'Newsletter 3', 'Newsletter 4'],
    relevantRoles: [AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT, ROLE_DIRIGEANT],
  },
  {
    rowKey: 'referent-mails',
    title: 'CAMPAGNE RÉFÉRENT — E-mail de présentation',
    description: 'Envoyez la campagne de présentation du référent mutation location aux copropriétaires.',
    route: ROUTES.COMMUNICATION_REFERENT_CAMPAIGNS,
    planningEventType: 'referent',
    planningEventKeys: ['Référent 1', 'Référent 2'],
    relevantRoles: [AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT, ROLE_DIRIGEANT],
  },
  {
    rowKey: 'ag-communication',
    title: 'AG — Communication lors des assemblées générales',
    description: 'Préparez les documents et argumentaires pour la communication pendant les AG.',
    route: ROUTES.COMMUNICATION_GENERAL_ASSEMBLY_REFERENT,
    frequency: 'on-demand',
    frequencyLabel: 'Selon le calendrier des AG',
    relevantRoles: [
      AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT,
      AttributionRole.GESTIONNAIRE, AttributionRole.ASSISTANT_GESTIONNAIRE,
      ROLE_DIRIGEANT,
    ],
  },
  {
    rowKey: 'dpe',
    title: 'TOUS LES MERCREDIS — Traitement des nouveaux DPE',
    description: 'Traitement des nouveaux DPE sortis sur votre parc — nouveaux diagnostics = nouvelles opportunités de contact RGPD conforme.',
    route: ROUTES.PROSPECTING_NEW_OPPORTUNITIES,
    frequency: 'weekly',
    frequencyLabel: 'Chaque mercredi',
    relevantRoles: [
      AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT, ROLE_DIRIGEANT,
    ],
  },
  {
    rowKey: 'sci-mails',
    title: 'SCI — Campagne de mails aux SCI',
    description: 'Envoyez les campagnes ciblées aux SCI pour proposer vos services en transaction ou gestion locative.',
    route: ROUTES.PROSPECTING_SCIS_REFERENT,
    planningEventType: 'sci-referent',
    planningEventKeys: ['SCI 1', 'SCI 2'],
    relevantRoles: [AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT, ROLE_DIRIGEANT],
  },
  {
    rowKey: 'boitage',
    title: 'BOÎTAGE — Distribution dans les copropriétés sélectionnées',
    description: 'Téléchargez le module défini par le cabinet et distribuez-le dans les immeubles sélectionnés.',
    warning: 'RAPPEL : ne pas distribuer dans les boîtes avec STOP PUB',
    route: ROUTES.PROSPECTING_BOITAGE_TRANSACTION,
    planningEventType: 'boitage',
    planningEventKeys: ['Boîtage 1', 'Boîtage 2'],
    relevantRoles: [
      AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT,
      AttributionRole.TRANSACTIONNAIRE, AttributionRole.INTERVENANT, ROLE_DIRIGEANT,
    ],
  },
  {
    rowKey: 'property-communication',
    title: 'Communication sur vos biens à vendre dans l\'immeuble',
    description: 'Informez les copropriétaires des biens à vendre dans leur immeuble via le boîtage.',
    route: ROUTES.PROSPECTING_SCIS_TRANSACTION,
    frequency: 'on-demand',
    frequencyLabel: 'À chaque nouveau mandat',
    relevantRoles: [
      AttributionRole.REFERENT, AttributionRole.ASSISTANT_REFERENT,
      AttributionRole.TRANSACTIONNAIRE, AttributionRole.INTERVENANT, ROLE_DIRIGEANT,
    ],
  },
];

const MONTHS_SHORT = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

function positionToDate(startMonth: number, position: number, year: number): Date {
  const monthOffset = Math.floor(position);
  const actualMonth = ((startMonth - 1 + monthOffset) % 12);
  const actualYear = year + Math.floor((startMonth - 1 + monthOffset) / 12);
  const fraction = position - monthOffset;
  let day: number;
  if (fraction < 0.33) { day = 5; } else if (fraction > 0.66) { day = 25; } else { day = 15; }
  return new Date(actualYear, actualMonth, day);
}

function getNextWednesday(from: Date): Date {
  const d = new Date(from);
  const dayOfWeek = d.getDay();
  const daysUntilWednesday = (3 - dayOfWeek + 7) % 7 || 7;
  d.setDate(d.getDate() + daysUntilWednesday);
  return d;
}

export function formatActionDate(date: Date): string {
  const day = date.getDate();
  const month = MONTHS_SHORT[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

function planningEventKey(planningEventType: string | undefined, eventKey: string): string {
  return `${planningEventType}-${eventKey}`;
}

// An occurrence switched off in the planning, or marked as done for this cycle,
// is no longer something to do.
function isOpenEvent(
  planningEventType: string | undefined,
  eventKey: string,
  timelineData: ToolsTimelineYear | null | undefined,
): boolean {
  const activation = timelineData?.eventActivation?.[planningEventType as keyof ToolsTimelineEventActivation];
  if (activation?.[eventKey] === false) { return false; }
  return !timelineData?.completedEvents?.[planningEventKey(planningEventType, eventKey)];
}

function resolveEventDate(
  eventKey: string,
  planningEventType: string | undefined,
  eventPositions: ToolsTimelineEventPositions,
  exactDates: ToolsTimelineExactDates | undefined,
  startMonth: number,
  year: number,
): Date | null {
  const ed = exactDates?.[planningEventKey(planningEventType, eventKey)] ?? exactDates?.[eventKey];
  if (ed) {
    // An exact date is stored as {day, month} only: the calendar year has to
    // be derived from the cycle, exactly like the position-based branch below.
    return new Date(calendarYearForMonth(ed.month, startMonth, year), ed.month - 1, ed.day);
  }
  if (!planningEventType) { return null; }
  const typePositions = eventPositions[planningEventType as keyof ToolsTimelineEventPositions];
  if (!typePositions || typeof typePositions !== 'object') { return null; }
  const posVal = (typePositions as Record<string, number | { position: number }>)[eventKey];
  const position = typeof posVal === 'object' && posVal !== null ? posVal.position : (typeof posVal === 'number' ? posVal : null);
  if (position === null || position === undefined) { return null; }
  return positionToDate(startMonth, position, year);
}

export function computeNextActions(
  timelineData: ToolsTimelineYear | null | undefined,
  personAssignments: ToolsTimelinePersonAssignments | undefined,
  currentUserId: number | null,
  maxActions: number = 3,
  now: Date = new Date(),
  userRoles?: string[],
  year: number = now.getFullYear(),
): NextAction[] {
  const actions: NextAction[] = [];
  const startMonth = timelineData?.startMonth ?? 9;
  const eventPositions = timelineData?.eventPositions;
  const exactDates = timelineData?.exactDates;

  for (const def of ALL_ACTION_DEFINITIONS) {
    // `userRoles === undefined` means "do not filter" (used for admins who see
    // everything). Any provided array — including an empty one (e.g. when the
    // roles endpoint returns nothing or 403s) — is filtered, so a user only ever
    // sees actions relevant to their roles instead of the full list.
    if (userRoles !== undefined) {
      if (!def.relevantRoles) { continue; }
      if (!def.relevantRoles.some((r) => userRoles.includes(r))) { continue; }
    }

    if (currentUserId && personAssignments) {
      const assignedTo = personAssignments[def.rowKey];
      if (assignedTo !== undefined && assignedTo !== currentUserId) { continue; }
    }

    const assignedUserId = personAssignments?.[def.rowKey] ?? null;

    if (def.frequency === 'weekly') {
      const nextWed = getNextWednesday(now);
      actions.push({
        id: `${def.rowKey}-weekly`,
        rowKey: def.rowKey,
        title: def.title,
        description: def.description,
        warning: def.warning,
        date: nextWed,
        frequencyLabel: def.frequencyLabel,
        route: def.route,
        assignedUserId,
        urgency: 'recurring',
      });
      continue;
    }

    if (def.frequency === 'on-demand') {
      actions.push({
        id: `${def.rowKey}-ondemand`,
        rowKey: def.rowKey,
        title: def.title,
        description: def.description,
        warning: def.warning,
        date: null,
        frequencyLabel: def.frequencyLabel,
        route: def.route,
        assignedUserId,
        urgency: 'recurring',
      });
      continue;
    }

    if (def.planningEventKeys && eventPositions) {
      let nearestDate: Date | null = null;
      let nearestEventKey = '';

      for (const eventKey of def.planningEventKeys) {
        if (!isOpenEvent(def.planningEventType, eventKey, timelineData)) { continue; }
        const eventDate = resolveEventDate(eventKey, def.planningEventType, eventPositions, exactDates, startMonth, year);
        if (eventDate && eventDate >= now && (!nearestDate || eventDate < nearestDate)) {
          nearestDate = eventDate;
          nearestEventKey = eventKey;
        }
      }

      if (nearestDate) {
        const daysUntil = Math.ceil((nearestDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        let urgency: NextAction['urgency'] = 'upcoming';
        if (daysUntil <= 0) { urgency = 'overdue'; } else if (daysUntil <= 14) { urgency = 'soon'; }

        actions.push({
          id: `${def.rowKey}-${nearestEventKey}`,
          rowKey: def.rowKey,
          title: `${def.title}`,
          description: def.description,
          warning: def.warning,
          date: nearestDate,
          route: def.route,
          assignedUserId,
          urgency,
        });
      }
    }
  }

  actions.sort((a, b) => {
    const urgencyOrder = { overdue: 0, soon: 1, recurring: 2, upcoming: 3 };
    const ua = urgencyOrder[a.urgency] ?? 4;
    const ub = urgencyOrder[b.urgency] ?? 4;
    if (ua !== ub) { return ua - ub; }
    if (a.date && b.date) { return a.date.getTime() - b.date.getTime(); }
    if (a.date) { return -1; }
    if (b.date) { return 1; }
    return 0;
  });

  return actions.slice(0, maxActions);
}

export function computeTeamActions(
  timelineData: ToolsTimelineYear | null | undefined,
  personAssignments: ToolsTimelinePersonAssignments | undefined,
  currentUserId: number | null,
  now: Date = new Date(),
  year: number = now.getFullYear(),
): TeamAction[] {
  if (!personAssignments || !currentUserId) { return []; }

  const actions: TeamAction[] = [];
  const startMonth = timelineData?.startMonth ?? 9;
  const eventPositions = timelineData?.eventPositions;
  const exactDates = timelineData?.exactDates;

  for (const def of ALL_ACTION_DEFINITIONS) {
    const assignedTo = personAssignments[def.rowKey];
    if (assignedTo === undefined || assignedTo === currentUserId) { continue; }

    let actionDate: Date | null = null;
    let actionEventKey: string | undefined;
    let actionFrequencyLabel: string | undefined;

    if (def.frequency === 'weekly') {
      actionDate = getNextWednesday(now);
      actionFrequencyLabel = def.frequencyLabel;
    } else if (def.frequency === 'on-demand') {
      actionDate = null;
      actionFrequencyLabel = def.frequencyLabel;
    } else if (def.planningEventKeys && eventPositions) {
      const openEventKeys = def.planningEventKeys.filter((eventKey) => isOpenEvent(def.planningEventType, eventKey, timelineData));
      // Every occurrence of the cycle is done (or switched off): the row comes
      // back with the next cycle's planning.
      if (openEventKeys.length === 0) { continue; }
      let nearestDate: Date | null = null;
      for (const eventKey of openEventKeys) {
        const eventDate = resolveEventDate(eventKey, def.planningEventType, eventPositions, exactDates, startMonth, year);
        if (eventDate && (!nearestDate || eventDate < nearestDate)) {
          nearestDate = eventDate;
          actionEventKey = planningEventKey(def.planningEventType, eventKey);
        }
      }
      actionDate = nearestDate;
    }

    const status: TeamAction['status'] = actionDate && actionDate < now ? 'overdue' : 'pending';

    actions.push({
      id: `team-${def.rowKey}`,
      rowKey: def.rowKey,
      eventKey: actionEventKey,
      title: def.title,
      description: def.description,
      date: actionDate,
      frequencyLabel: actionFrequencyLabel,
      route: def.route,
      assignedUserId: assignedTo,
      status,
    });
  }

  actions.sort((a, b) => {
    if (a.status !== b.status) { return a.status === 'overdue' ? -1 : 1; }
    if (a.date && b.date) { return a.date.getTime() - b.date.getTime(); }
    if (a.date) { return -1; }
    if (b.date) { return 1; }
    return 0;
  });

  return actions;
}
